import { alpha, styled } from '@mui/material';
import { Resizable } from 're-resizable';
import { ReactNode } from 'react';
import { EmbedRendererProps, ResizableEmbedRendererProps } from './types';

const Container = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',

  '& > div': {
    margin: '0 auto',
  },

  '&:hover .handle': {
    backgroundColor: alpha(
      theme.palette.action.selected,
      theme.palette.action.selectedOpacity,
    ),
    boxShadow: theme.shadows[1],
  },

  '.handle': {
    position: 'absolute',

    '--width': '8px',
    '--height': theme.spacing(8),
    '--margin': '8px',
    '--left': 'auto',
    '--top': 'auto',
    '--right': 'auto',
    '--bottom': 'auto',

    width: 'var(--width) !important',
    height: 'var(--height) !important',

    top: 'var(--top) !important',
    right: 'var(--right) !important',
    bottom: 'var(--bottom) !important',
    left: 'var(--left) !important',
    transition: theme.transitions.create(['background-color', 'box-shadow']),

    borderRadius: '4px',
    cursor: 'ew-resize',

    '&:hover': {
      backgroundColor: alpha(
        theme.palette.action.active,
        theme.palette.action.activatedOpacity,
      ),
      boxShadow: theme.shadows[2],
    },

    '&.--sw': {
      bottom: '-4px',
      left: '-4px',
      cursor: 'sw-resize',
    },
    '&.--se': {
      bottom: '-4px',
      right: '-4px',
      cursor: 'se-resize',
    },
    '&.--nw': {
      top: '-4px',
      left: '-4px',
      cursor: 'nw-resize',
    },
    '&.--ne': {
      top: '-4px',
      right: '-4px',
      cursor: 'ne-resize',
    },
    '&.--w, &.--e': {
      cursor: 'ew-resize',
      '--top': '50%',
    },
    '&.--w': {
      '--left': 'var(--margin)',
    },
    '&.--e': {
      '--right': 'var(--margin)',
    },
    '&.--n, &.--s': {
      '--left': 'calc(50% - var(--width) / 2)',
      '--height': '8px',
      '--width': theme.spacing(8),
      cursor: 'ns-resize',
    },
    '&.--n': {
      '--top': 'var(--margin)',
    },
    '&.--s': {
      '--bottom': 'var(--margin)',
    },
  },
}));

type HoCOptions = {
  /**
   * @default 'true'
   */
  lockAspectRatio?: boolean;
  /**
   * @default 'auto'
   */
  defaultHeight?: number | string;
  /**
   * @default 'auto'
   */
  defaultWidth?: number | string;
};

type NodeResizableProps<Metadata extends object> =
  ResizableEmbedRendererProps<Metadata> &
    HoCOptions & {
      children: ReactNode;
    };

export const NodeResizable = <Metadata extends object>({
  children,
  metadata,
  onMetadataChange,
  lockAspectRatio = true,
  defaultHeight = 'auto',
  defaultWidth = 'auto',
}: NodeResizableProps<Metadata>) => (
  <Container>
    <Resizable
      lockAspectRatio={lockAspectRatio}
      defaultSize={{
        width: metadata?.width || defaultWidth,
        height: metadata?.height || defaultHeight,
      }}
      handleClasses={{
        top: 'handle --n',
        right: 'handle --e',
        bottom: 'handle --s',
        left: 'handle --w',
        topRight: 'handle --ne',
        bottomRight: 'handle --se',
        bottomLeft: 'handle --sw',
        topLeft: 'handle --nw',
      }}
      onResizeStop={(_e, _dir, ref) => {
        onMetadataChange({
          ...metadata,
          width: +ref.style.width.replace('px', ''),
          height: +ref.style.height.replace('px', ''),
        });
      }}
      boundsByDirection
    >
      {children}
    </Resizable>
  </Container>
);

export const withNodeResizable =
  <Metadata extends object>(
    Component: React.ComponentType<EmbedRendererProps<Metadata>>,
    opts: HoCOptions = {},
  ): any =>
  (props: NodeResizableProps<Metadata>) => (
    <NodeResizable {...props} {...opts}>
      <Component {...(props as any)} />
    </NodeResizable>
  );
